/**
 * <Flashcard> — universal renderer for all 5 card types.
 * Handles the flip animation, cloze reconstruction UI, and Feynman
 * text-entry with AI-graded diff on reveal.
 */
import React, { useMemo, useState } from 'react';
import { STUDY_THEME, CARD_TYPE_META } from './studyTheme';

const clozeRender = (front, gaps, filled) => {
  // Replace runs of underscores in `front` with the matching input.
  // `gaps` is the ground truth list; `filled` is what the user typed
  // (same length). On reveal we show inline colored diff.
  const parts = front.split(/(_{2,})/g);
  let gi = 0;
  return parts.map((p, i) => {
    if (!/^_+$/.test(p)) return <span key={i}>{p}</span>;
    const idx = gi++;
    const truth = gaps[idx] || '';
    const val = filled[idx] || '';
    return (
      <span
        key={i}
        style={{
          display: 'inline-block', minWidth: 84,
          padding: '2px 6px', margin: '0 3px',
          borderBottom: `2px solid ${STUDY_THEME.goldDeep}`,
          fontWeight: 700, letterSpacing: 0.3,
          background: 'rgba(252,211,89,0.15)',
          borderRadius: '3px 3px 0 0',
        }}
        data-testid={`cloze-gap-${idx}`}
      >
        {val || <span style={{ opacity: 0.35 }}>·&nbsp;{truth.length}&nbsp;chars&nbsp;·</span>}
      </span>
    );
  });
};

export function Flashcard({
  card, revealed, back, onFlip, onRate, ratings, onAskTutor, onHearIt,
  onSpeakAnswer,
}) {
  const meta = CARD_TYPE_META[card.type] || CARD_TYPE_META.standard;
  // Cloze input state — one string per gap.
  const [clozeInputs, setClozeInputs] = useState(
    () => (card.cloze_gaps || []).map(() => '')
  );
  // Feynman answer — a single long-form string.
  const [feynmanAnswer, setFeynmanAnswer] = useState('');

  const diffFeynman = useMemo(() => {
    // Very lightweight "did you cover the key phrases" grader. The
    // backend's rubric in `card.back` includes a `Rubric — …` header
    // and bullet lines. We check which key phrases show up in the
    // student's answer (case-insensitive substring). Deeper grading
    // (Claude-powered) is a Phase 2 upgrade; this gives instant feedback.
    if (!revealed || !back || card.type !== 'feynman') return null;
    const rubricLines = back
      .split('\n')
      .filter((l) => l.trim().startsWith('•'))
      .map((l) => l.replace(/^•\s*/, '').trim());
    const answerLower = feynmanAnswer.toLowerCase();
    const hits = rubricLines.map((line) => {
      // Pull "key phrase" heuristic: first 2 significant words after a marker
      const norm = line.toLowerCase();
      const words = norm.split(/[\s,.:;()]+/).filter(w => w.length > 3);
      const covered = words.slice(0, 2).some(w => answerLower.includes(w));
      return { line, covered };
    });
    const score = hits.filter((h) => h.covered).length;
    return { hits, score, total: rubricLines.length };
  }, [revealed, back, feynmanAnswer, card.type]);

  return (
    <div
      data-testid="flashcard"
      data-card-type={card.type}
      style={{
        background: 'white',
        borderRadius: 16,
        boxShadow: STUDY_THEME.shadow,
        borderTop: `6px solid ${meta.accent}`,
        padding: '32px 36px',
        maxWidth: 720,
        width: '100%',
        margin: '0 auto',
      }}
    >
      {/* Type badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <span
          data-testid="card-type-badge"
          style={{
            fontSize: 12, letterSpacing: 1, textTransform: 'uppercase',
            fontWeight: 700, color: meta.accent,
            background: meta.bg, padding: '4px 12px',
            borderRadius: 999,
          }}
          title={meta.tooltip}
        >
          {meta.emoji} {meta.label}
        </span>
        {(card.related_modules || []).map((m) => (
          <span
            key={m}
            style={{
              fontSize: 11, color: STUDY_THEME.goldDeep,
              background: 'rgba(252,211,89,0.20)',
              padding: '3px 9px', borderRadius: 4, fontWeight: 600,
            }}
          >
            {m}
          </span>
        ))}
      </div>

      {/* Front — question / prompt */}
      <div
        data-testid="card-front"
        style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: card.type === 'feynman' ? 22 : 24,
          lineHeight: 1.4, color: STUDY_THEME.ink,
          marginBottom: 24,
        }}
      >
        {card.type === 'cloze'
          ? clozeRender(card.front, card.cloze_gaps || [], clozeInputs)
          : card.front}
      </div>

      {/* Cloze input row */}
      {card.type === 'cloze' && !revealed && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          {(card.cloze_gaps || []).map((truth, i) => (
            <input
              key={i}
              value={clozeInputs[i]}
              placeholder={`Gap ${i + 1}`}
              onChange={(e) => {
                const next = [...clozeInputs];
                next[i] = e.target.value;
                setClozeInputs(next);
              }}
              data-testid={`cloze-input-${i}`}
              style={{
                padding: '10px 14px', fontSize: 15, borderRadius: 8,
                border: `1px solid ${STUDY_THEME.line}`,
                background: 'white', minWidth: 140,
                fontFamily: 'inherit', color: STUDY_THEME.ink,
              }}
            />
          ))}
        </div>
      )}

      {/* Feynman input */}
      {card.type === 'feynman' && !revealed && (
        <textarea
          value={feynmanAnswer}
          onChange={(e) => setFeynmanAnswer(e.target.value)}
          placeholder="Explain in your own words — as if teaching a friend who's never taken this course…"
          data-testid="feynman-input"
          style={{
            width: '100%', minHeight: 140,
            padding: '12px 14px', fontSize: 15, lineHeight: 1.5,
            borderRadius: 8, border: `1px solid ${STUDY_THEME.line}`,
            background: 'white', color: STUDY_THEME.ink,
            fontFamily: 'inherit', resize: 'vertical',
            marginBottom: 20,
          }}
        />
      )}

      {/* Reveal / Back */}
      {!revealed && (
        <button
          onClick={onFlip}
          data-testid="reveal-btn"
          style={{
            padding: '14px 28px', fontSize: 15, fontWeight: 700,
            letterSpacing: 0.3, background: STUDY_THEME.ink,
            color: 'white', border: 'none', borderRadius: 8,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Show Answer
        </button>
      )}

      {revealed && (
        <div
          data-testid="card-back"
          style={{
            paddingTop: 20, marginTop: 12,
            borderTop: `1px dashed ${STUDY_THEME.line}`,
          }}
        >
          <div
            style={{
              fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
              fontWeight: 700, color: meta.accent,
              marginBottom: 10,
            }}
          >
            Answer
          </div>
          <div style={{
            fontSize: 15.5, lineHeight: 1.65, color: STUDY_THEME.inkSoft,
            whiteSpace: 'pre-wrap', marginBottom: 20,
          }}>
            {back}
          </div>

          {/* Feynman diff */}
          {diffFeynman && (
            <div style={{
              background: STUDY_THEME.paperWarm, padding: 16, borderRadius: 10,
              marginBottom: 20, fontSize: 13.5, lineHeight: 1.55,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 8, color: STUDY_THEME.goldDeep }}>
                Rubric coverage: {diffFeynman.score} / {diffFeynman.total}
              </div>
              {diffFeynman.hits.map((h, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                  <span style={{ color: h.covered ? '#16A34A' : '#DC2626', fontWeight: 700 }}>
                    {h.covered ? '✓' : '·'}
                  </span>
                  <span style={{ opacity: h.covered ? 1 : 0.7 }}>{h.line}</span>
                </div>
              ))}
            </div>
          )}

          {/* Bottom action row */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
            {onHearIt && (
              <button
                onClick={onHearIt}
                data-testid="hear-professor-btn"
                style={smallBtn}
              >
                🎧 Hear the professor say it
              </button>
            )}
            {onAskTutor && (
              <button
                onClick={onAskTutor}
                data-testid="ask-tutor-btn"
                style={smallBtn}
              >
                🤖 Ask AI Tutor about this card
              </button>
            )}
          </div>

          {/* Rating row */}
          {ratings && (
            <div
              data-testid="rating-row"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}
            >
              {ratings.map((r) => (
                <button
                  key={r.rating}
                  onClick={() => onRate(r.rating)}
                  data-testid={`rating-${r.rating}`}
                  title={`${r.hint} (${r.key})`}
                  style={{
                    padding: '14px 8px', fontSize: 14, fontWeight: 700,
                    color: r.accent, background: r.bg,
                    border: `1px solid ${r.accent}`, borderRadius: 8,
                    cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', flexDirection: 'column', gap: 2,
                  }}
                >
                  <span>{r.label}</span>
                  <span style={{ fontSize: 11, opacity: 0.75, fontWeight: 500 }}>
                    Press {r.key}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const smallBtn = {
  fontSize: 13, padding: '8px 14px',
  background: 'white', color: STUDY_THEME.inkSoft,
  border: `1px solid ${STUDY_THEME.line}`, borderRadius: 6,
  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
};

export default Flashcard;
